import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PiggyBank,
  Home,
  ShoppingBag,
  Wallet,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Calendar
} from 'lucide-react';
import { useInsightsQuery } from '../../hooks/useQueries';
import Modal from '../common/Modal';

// Format amount in Indian Rupees
const formatAmount = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

export default function InsightsModal({ isOpen, onClose }) {
  const navigate = useNavigate();
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  const isCurrentMonth = selectedMonth === now.getMonth() + 1 && selectedYear === now.getFullYear();

  const goToPrevMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(y => y - 1);
    } else {
      setSelectedMonth(m => m - 1);
    }
  };

  const goToNextMonth = () => {
    if (isCurrentMonth) return;
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(y => y + 1);
    } else {
      setSelectedMonth(m => m + 1);
    }
  };

  const monthLabel = new Date(selectedYear, selectedMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const { data, isLoading, isError, refetch, isFetching } = useInsightsQuery(
    { month: selectedMonth, year: selectedYear },
    { enabled: isOpen }
  );

  // Loading content
  const loadingContent = (
    <div className="space-y-4">
      <div className="h-32 bg-surface-100 rounded-xl animate-pulse" />
      <div className="h-20 bg-surface-100 rounded-xl animate-pulse" />
      <div className="grid grid-cols-2 gap-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-24 bg-surface-100 rounded-xl animate-pulse" />
        ))}
      </div>
    </div>
  );

  // Error content
  const errorContent = (
    <div className="text-center py-12">
      <div className="w-14 h-14 rounded-full bg-expense-100 flex items-center justify-center mx-auto mb-3">
        <AlertCircle className="w-7 h-7 text-expense-600" />
      </div>
      <h2 className="text-base font-semibold text-surface-900 mb-1">Unable to Load</h2>
      <p className="text-sm text-surface-500 mb-4">Please try again.</p>
      <button
        onClick={() => refetch()}
        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors"
        style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
      >
        <RefreshCw className="w-4 h-4" />
        Try Again
      </button>
    </div>
  );

  // Month navigator
  const monthNavigator = (
    <div className="bg-surface-50 rounded-xl border border-surface-200 p-3 mb-4">
      <div className="flex items-center justify-between">
        <button
          onClick={goToPrevMonth}
          className="w-9 h-9 rounded-xl flex items-center justify-center bg-white hover:bg-surface-100 active:scale-95 transition-all border border-surface-200"
        >
          <ChevronLeft className="w-4 h-4 text-surface-600" />
        </button>
        <div className="flex flex-col items-center">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-surface-400" />
            <span className="text-sm font-bold text-surface-900">{monthLabel}</span>
          </div>
          {isCurrentMonth && (
            <span className="mt-0.5 px-2 py-0.5 rounded-full text-[10px] font-medium bg-primary-100 text-primary-600">Current Month</span>
          )}
        </div>
        <button
          onClick={goToNextMonth}
          disabled={isCurrentMonth}
          className="w-9 h-9 rounded-xl flex items-center justify-center bg-white hover:bg-surface-100 active:scale-95 transition-all border border-surface-200 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronRight className="w-4 h-4 text-surface-600" />
        </button>
      </div>
    </div>
  );

  // Empty content
  const emptyContent = (
    <div>
      {monthNavigator}
      <div className="text-center py-8 bg-gradient-to-b from-primary-50 to-white rounded-xl border border-primary-100">
        <div className="w-14 h-14 rounded-xl bg-primary-100 flex items-center justify-center mx-auto mb-4">
          <PiggyBank className="w-7 h-7 text-primary-500" />
        </div>
        <h2 className="text-base font-semibold text-surface-900 mb-2">No Data for {monthLabel}</h2>
        <p className="text-sm text-surface-500 max-w-xs mx-auto mb-1 px-4">
          No income or expenses found for this month.
        </p>
        <p className="text-xs text-surface-400 px-4">
          Use the arrows above to navigate to a different month.
        </p>
      </div>
    </div>
  );

  const { breakdown, percentages, bestMatch, period } = data || {};

  // Main content
  const mainContent = (
    <div className="space-y-4">
      {/* Month Navigation */}
      {monthNavigator}

      {/* Your Spending Summary */}
      <div className="bg-surface-50 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-surface-900 mb-3">Your Spending This Month</h3>

        {/* Income */}
        <div className="flex items-center justify-between mb-3 pb-3 border-b border-surface-200">
          <div className="flex items-center gap-2">
            <Wallet className="w-4 h-4 text-income-600" />
            <span className="text-sm text-surface-600">Income</span>
          </div>
          <span className="text-sm font-bold text-income-600">{formatAmount(breakdown?.income || 0)}</span>
        </div>

        {/* Needs/Wants/Savings breakdown */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Home className="w-4 h-4 text-blue-500" />
              <span className="text-sm text-surface-600">Needs</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-surface-900">{formatAmount(breakdown?.needs || 0)}</span>
              <span className="text-xs text-surface-500">({percentages?.needs || 0}%)</span>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShoppingBag className="w-4 h-4 text-purple-500" />
              <span className="text-sm text-surface-600">Wants</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-surface-900">{formatAmount(breakdown?.wants || 0)}</span>
              <span className="text-xs text-surface-500">({percentages?.wants || 0}%)</span>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <PiggyBank className="w-4 h-4 text-income-500" />
              <span className="text-sm text-surface-600">Savings</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-sm font-medium ${(breakdown?.savings || 0) >= 0 ? 'text-income-600' : 'text-expense-600'}`}>
                {formatAmount(breakdown?.savings || 0)}
              </span>
              <span className="text-xs text-surface-500">({percentages?.savings || 0}%)</span>
            </div>
          </div>
        </div>
      </div>

      {/* Best Match Card */}
      {bestMatch && (
        <div
          className="rounded-xl p-4 border-2"
          style={{ backgroundColor: 'var(--accent-bg)', borderColor: 'var(--accent-text)', opacity: 0.9 }}
        >
          <div className="flex items-center gap-3">
            <span className="text-3xl">{bestMatch.emoji}</span>
            <div className="flex-1">
              <p className="text-xs opacity-80" style={{ color: 'var(--accent-text)' }}>Your spending matches</p>
              <p className="font-bold" style={{ color: 'var(--accent-text)' }}>{bestMatch.name}</p>
              <p className="text-xs opacity-70 mt-0.5" style={{ color: 'var(--accent-text)' }}>{bestMatch.description}</p>
            </div>
            <div
              className="px-3 py-1.5 rounded-full text-sm font-bold"
              style={{ backgroundColor: 'var(--accent-text)', color: 'var(--accent-bg)' }}
            >
              {bestMatch.matchScore}%
            </div>
          </div>
        </div>
      )}

      {/* Savings Status */}
      {(breakdown?.savings || 0) < 0 ? (
        <div className="p-3 bg-expense-50 rounded-lg border border-expense-200">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-expense-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-expense-700">Spending more than earning</p>
              <p className="text-xs text-expense-600 mt-0.5">
                You've spent {formatAmount(Math.abs(breakdown?.savings || 0))} more than your income.
              </p>
            </div>
          </div>
        </div>
      ) : (percentages?.savings || 0) >= 20 ? (
        <div className="p-3 bg-income-50 rounded-lg border border-income-200">
          <div className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-income-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-income-700">Great savings rate!</p>
              <p className="text-xs text-income-600 mt-0.5">
                You're saving {percentages?.savings}% of your income.
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {/* View Full Analysis Link */}
      <button
        onClick={() => navigate('/insights')}
        className="flex items-center justify-center gap-2 p-3 rounded-xl text-sm font-semibold transition-colors w-full"
        style={{
          backgroundColor: 'var(--accent-light)',
          color: 'var(--accent-light-text)',
        }}
      >
        View Full Spending Insights
        <ExternalLink className="w-4 h-4" />
      </button>

      {/* Period Info */}
      {period?.current && (
        <p className="text-xs text-surface-500 text-center">
          Analysis for {period.current.month}
        </p>
      )}
    </div>
  );

  // Determine content to render
  let content;
  if (isLoading) {
    content = loadingContent;
  } else if (isError) {
    content = errorContent;
  } else if (!breakdown?.income && !breakdown?.needs && !breakdown?.wants) {
    content = emptyContent;
  } else {
    content = mainContent;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <span>Spending Insights</span>
          <span className="text-lg">{bestMatch?.emoji || '💰'}</span>
          {!isLoading && !isError && (
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="ml-2 p-1.5 bg-white/20 text-white/80 hover:text-white hover:bg-white/30 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
      }
      size="md"
    >
      {content}
    </Modal>
  );
}
